// @ts-check
import { extend, h, uid } from "./dom.js";
import { icon } from "./icons.js";

/**
 * Select (DS-04): a button that opens a listbox popover, replacing native
 * <select>. Lists longer than eight options get a search box. Keyboard: Enter,
 * Space, ArrowDown open; arrows move; Enter selects; Escape closes; typing jumps
 * to the next matching option. A hidden input carries `name` for FormData, and
 * the root element dispatches a bubbling "change" event.
 *
 * @typedef {{ value: string, text: string, description?: string, disabled?: boolean }} SelectOption
 * @param {{
 *   label: string, options: SelectOption[], value?: string, name?: string,
 *   placeholder?: string, onChange?: (value: string) => void, searchable?: boolean,
 *   disabled?: boolean, size?: "sm" | "md", className?: string,
 * }} options
 */
export function select({
  label,
  options,
  value = "",
  name,
  placeholder = "Choose…",
  onChange,
  searchable,
  disabled = false,
  size = "md",
  className = "",
}) {
  const id = uid("select");
  const valueText = h("span", { class: "ui-choice__value", id: `${id}-value` });
  const trigger = h(
    "button",
    {
      type: "button",
      id: `${id}-trigger`,
      class: "ui-choice__trigger",
      // The name is the field; the chosen value is read as its description.
      aria: {
        haspopup: "listbox",
        expanded: "false",
        label,
        describedby: `${id}-value`,
      },
      dataset: { size },
    },
    valueText,
    icon("chevron-down"),
  );
  const list = h("div", {
    class: "ui-choice__list",
    role: "listbox",
    id: `${id}-list`,
    tabIndex: -1,
    aria: { label },
  });
  const search = h("input", {
    type: "search",
    class: "ui-choice__search",
    placeholder: "Search…",
    autocomplete: "off",
    role: "combobox",
    aria: {
      label: `Search ${label}`,
      controls: list.id,
      autocomplete: "list",
      expanded: "true",
    },
  });
  const empty = h("p", {
    class: "ui-choice__empty",
    text: "No matching options.",
    hidden: true,
  });
  const popup = h(
    "div",
    {
      class: "ui-popover ui-choice__popup",
      popover: "auto",
      id: `${id}-popup`,
    },
    search,
    list,
    empty,
  );
  const hidden = h("input", { type: "hidden", name });
  const root = h(
    "div",
    { class: `ui-choice${className ? ` ${className}` : ""}` },
    trigger,
    hidden,
    popup,
  );
  trigger.setAttribute("aria-controls", popup.id);

  let items = options;
  let current = value;
  let active = -1;
  /** @type {SelectOption[]} */
  let visible = [];
  let typed = "";
  let typedAt = 0;
  const useSearch = () => searchable ?? items.length > 8;

  function label_() {
    const chosen = items.find((option) => option.value === current);
    valueText.textContent = chosen ? chosen.text : placeholder;
    valueText.dataset.placeholder = chosen ? "false" : "true";
    hidden.value = current;
  }
  function renderList() {
    const query = search.value.trim().toLocaleLowerCase();
    visible = items.filter(
      (option) =>
        !query ||
        `${option.text} ${option.description || ""}`
          .toLocaleLowerCase()
          .includes(query),
    );
    list.replaceChildren(
      ...visible.map((option, index) =>
        h(
          "div",
          {
            role: "option",
            id: `${id}-option-${index}`,
            class: "ui-choice__option",
            aria: {
              selected: String(option.value === current),
              disabled: option.disabled ? "true" : undefined,
            },
            dataset: { active: String(index === active) },
            onPointerdown: (/** @type {PointerEvent} */ event) =>
              event.preventDefault(),
            onClick: () => !option.disabled && choose(option.value),
          },
          h(
            "span",
            { class: "ui-choice__check" },
            option.value === current && icon("check"),
          ),
          h(
            "span",
            { class: "ui-choice__text" },
            h("span", { text: option.text }),
            option.description && h("small", { text: option.description }),
          ),
        ),
      ),
    );
    empty.hidden = visible.length > 0;
    const owner = useSearch() ? search : list;
    const row = list.children[active];
    if (row) {
      owner.setAttribute("aria-activedescendant", row.id);
      row.scrollIntoView?.({ block: "nearest" });
    } else owner.removeAttribute("aria-activedescendant");
  }
  function open() {
    if (trigger.disabled) return;
    search.hidden = !useSearch();
    search.value = "";
    active = Math.max(
      0,
      items.findIndex((option) => option.value === current),
    );
    renderList();
    popup.showPopover();
    place();
    trigger.setAttribute("aria-expanded", "true");
    (useSearch() ? search : list).focus();
  }
  function close(restore = true) {
    if (popup.matches(":popover-open")) popup.hidePopover();
    trigger.setAttribute("aria-expanded", "false");
    if (restore) trigger.focus();
  }
  function place() {
    const box = trigger.getBoundingClientRect();
    const height = popup.offsetHeight;
    const below =
      box.bottom + 4 + height <= innerHeight - 8 || box.top < height + 12;
    popup.style.setProperty(
      "--select-left",
      `${Math.min(box.left, innerWidth - popup.offsetWidth - 8)}px`,
    );
    popup.style.setProperty(
      "--select-top",
      `${below ? box.bottom + 4 : box.top - 4 - height}px`,
    );
    popup.style.setProperty("--select-width", `${box.width}px`);
  }
  /** @param {string} next */
  function choose(next) {
    close();
    if (next === current) return;
    current = next;
    label_();
    onChange?.(next);
    root.dispatchEvent(new Event("change", { bubbles: true }));
  }
  /** @param {number} step */
  function move(step) {
    if (!visible.length) return;
    let index = active;
    for (let tries = 0; tries < visible.length; tries++) {
      index = (index + step + visible.length) % visible.length;
      if (!visible[index].disabled) break;
    }
    active = index;
    renderList();
  }
  trigger.addEventListener("click", () =>
    popup.matches(":popover-open") ? close() : open(),
  );
  trigger.addEventListener("keydown", (event) => {
    if (["ArrowDown", "ArrowUp"].includes(event.key)) {
      event.preventDefault();
      open();
    }
  });
  /** @param {KeyboardEvent} event */
  function keys(event) {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      move(event.key === "ArrowDown" ? 1 : -1);
    } else if (event.key === "Home" || event.key === "End") {
      event.preventDefault();
      active = event.key === "Home" ? 0 : visible.length - 1;
      renderList();
    } else if (event.key === "Enter" || (event.key === " " && !useSearch())) {
      event.preventDefault();
      const option = visible[active];
      if (option && !option.disabled) choose(option.value);
    } else if (event.key === "Escape") {
      event.preventDefault();
      close();
    } else if (event.key === "Tab") close(false);
    else if (!useSearch() && event.key.length === 1) {
      const now = performance.now();
      typed = now - typedAt > 600 ? event.key : typed + event.key;
      typedAt = now;
      const match = visible.findIndex((option) =>
        option.text.toLocaleLowerCase().startsWith(typed.toLocaleLowerCase()),
      );
      if (match >= 0) {
        active = match;
        renderList();
      }
    }
  }
  list.addEventListener("keydown", keys);
  search.addEventListener("keydown", keys);
  search.addEventListener("input", () => {
    active = 0;
    renderList();
  });
  popup.addEventListener("toggle", (event) => {
    if (/** @type {ToggleEvent} */ (event).newState === "closed")
      trigger.setAttribute("aria-expanded", "false");
  });
  trigger.disabled = disabled;
  label_();
  return extend(root, {
    get value() {
      return current;
    },
    set value(next) {
      current = next;
      label_();
    },
    /** Visible text of the chosen option. */
    get text() {
      return items.find((option) => option.value === current)?.text || "";
    },
    get disabled() {
      return trigger.disabled;
    },
    set disabled(next) {
      trigger.disabled = next;
    },
    /** @param {SelectOption[]} next */
    setOptions(next) {
      items = next;
      label_();
      if (popup.matches(":popover-open")) {
        const wasSearch = !search.hidden;
        search.hidden = !useSearch();
        if (search.hidden) search.value = "";
        active = Math.max(0, Math.min(active, items.length - 1));
        renderList();
        place();
        if (wasSearch !== useSearch()) (useSearch() ? search : list).focus();
      }
    },
    focus() {
      trigger.focus();
    },
    trigger,
  });
}
